import { useCallback, useMemo, useState } from "react"
import {
  Box,
  Stepper,
  Typography,
  FormControlLabel,
  Paper,
  Switch,
  useTheme,
  Slide,
  Backdrop,
  CircularProgress,
  Button,
} from "@mui/material"
import { useTranslation } from "providers/i18n"
import { Trans } from "react-i18next"
import { Link } from "react-router-dom"

import { useVenue } from "providers/venue"
import { OrdinalSelector } from "components/OrdinalSelector"
import {
  DestinationMarkerIcon,
  ExitIcon,
  OriginMarkerIcon,
} from "components/icons"
import {
  DirectionStepItem,
  DirectionCardActions,
} from "components/domains/direction"
import { MyLocationButton } from "components/Button"

import { QRCode } from "components/QRCode"

import { useDirectionOverview } from "hooks/direction/useDirectionOverview"
import { useDirectionOverviewMap } from "hooks/direction/useDirectionOverviewMap"
import { useMobileUrl } from "hooks/useMobileUrl"
import { useAppUI } from "providers/ui"
import { getIcon } from "utils/icon"
import { DirectionSummary } from "extensions/iconsiam/components/domains/direction/kiosk-v/DirectionSummary"

export const DirectionOverviewKioskVPage = () => {
  const [open, setOpen] = useState(false)
  const { isMultiOrdinalVenue } = useVenue()

  const {
    isCalculatingRoute,
    overviewSteps,
    duration,
    distance,
    currentOrdinal,
    originData,
    originName,
    originVenueLogo,
    destinationData,
    destinationName,
    destinationVenueLogo,
    elevatorPreferredMode,
    relatedLevelWithSteps,
    route,
    changeOrdinal,
    toggleElevatorPreference,
    startNavigate,
  } = useDirectionOverview()

  const stepsAvailable = overviewSteps && overviewSteps.length > 0

  useDirectionOverviewMap(route, currentOrdinal, {
    offset: { bottom: 411, top: 0, left: 0, right: 0 },
    enabled: !!stepsAvailable,
  })

  const { t } = useTranslation()

  const { goBack } = useAppUI()

  const { mobileUrl } = useMobileUrl()

  const theme = useTheme()

  const { paper, formControlLabel } =
    theme.components[`AppDirectionSearch`]?.styleOverrides || {}
  const {
    actionButtonWrapper,
    container,
    ordinalSelectorWrapper,
    stepper,
    stepOverviewContainer,
    wrapper,
    qrCodeContainer,
    directionOverviewContainer,
    directionContentWrapper,
    exitButton,
    locationButtonContainer,
    stepOverviewExitButton,
  } = theme.components[`AppDirectionOverview`]?.styleOverrides || {}

  const { originIcon, destinationIcon, highlightedText } =
    theme.components[`DirectionStep`]?.styleOverrides || {}

  const highlightedStepText = (
    <Typography variant="subTitle" component="span" sx={highlightedText} />
  )

  const originIconComp = useMemo(
    () =>
      getIcon(theme?.assets?.navigation?.origin, originIcon) || (
        <OriginMarkerIcon sx={originIcon} />
      ),
    [theme, originIcon]
  )

  const destinationIconComp = useMemo(
    () =>
      getIcon(theme?.assets?.navigation?.destination, destinationIcon) || (
        <DestinationMarkerIcon sx={destinationIcon} />
      ),
    [theme, destinationIcon]
  )

  const handleClickExit = useCallback(() => {
    goBack()
  }, [goBack])

  const exitIcon = useMemo(
    () => getIcon(theme?.assets?.exitIcon) || <ExitIcon />,
    [theme]
  )

  const handleClickStep = useCallback(() => setOpen((prev) => !prev), [])
  return (
    <>
      <Backdrop open={isCalculatingRoute}>
        <CircularProgress />
      </Backdrop>
      {stepsAvailable && (
        <>
          <Slide
            direction="up"
            in={stepsAvailable && !open}
            mountOnEnter
            unmountOnExit
          >
            <Box sx={container}>
              <Box sx={ordinalSelectorWrapper}>
                <OrdinalSelector
                  levels={relatedLevelWithSteps}
                  onClickOrdinal={changeOrdinal}
                  selectedOrdinal={currentOrdinal}
                />
              </Box>
              <Box sx={locationButtonContainer}>
                <MyLocationButton
                  component={Link}
                  to={`/maps/dir/unknown/${destinationData?.id}`}
                />
              </Box>
              <Box sx={actionButtonWrapper}>
                <Paper elevation={1} sx={paper}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={elevatorPreferredMode}
                        onChange={toggleElevatorPreference}
                      />
                    }
                    label={t("Avoid Steps")}
                    componentsProps={{ typography: { variant: "body2" } }}
                    sx={formControlLabel}
                  />
                </Paper>
                <Button
                  onClick={handleClickExit}
                  startIcon={exitIcon}
                  sx={exitButton}
                  variant="contained"
                >
                  {t("Exit")}
                </Button>
              </Box>
              <Paper sx={directionOverviewContainer}>
                <Box sx={wrapper}>
                  <Box sx={directionContentWrapper}>
                    <DirectionSummary
                      duration={duration}
                      distance={distance}
                      originName={originName}
                      destinationName={destinationName}
                      originVenueLogo={originVenueLogo}
                      destinationVenueLogo={destinationVenueLogo}
                    />
                    {mobileUrl && (
                      <QRCode
                        sx={qrCodeContainer}
                        url={mobileUrl}
                        caption={t("Scan Here")}
                        secondaryCaption={t("For Mobile")}
                      />
                    )}
                  </Box>
                  <DirectionCardActions
                    startButtonProps={{
                      className: "highlight",
                    }}
                    onStart={startNavigate}
                    onStep={handleClickStep}
                  />
                </Box>
              </Paper>
            </Box>
          </Slide>

          <Slide direction="up" in={open} mountOnEnter unmountOnExit>
            <Box sx={container}>
              <Box sx={actionButtonWrapper}>
                <Button
                  onClick={handleClickExit}
                  startIcon={exitIcon}
                  sx={stepOverviewExitButton}
                  variant="contained"
                >
                  {t("Exit")}
                </Button>
              </Box>
              <Paper sx={{ width: "100%" }}>
                <Box sx={stepOverviewContainer}>
                  <Stepper sx={stepper} orientation="vertical">
                    {originData && (
                      <DirectionStepItem
                        primaryLabel={
                          <Trans
                            components={{
                              location: highlightedStepText,
                            }}
                          >
                            {isMultiOrdinalVenue
                              ? t("Location Floor", {
                                  location: t(originName),
                                  level: originData.level,
                                })
                              : t(originName)}
                          </Trans>
                        }
                        secondaryLabel={t("Your last scanned location")}
                        stepLabelProps={{
                          icon: originIconComp,
                        }}
                      />
                    )}
                    {overviewSteps.map((step, index) => {
                      const { description } = step
                      const stepIndex = index + 1
                      return (
                        <DirectionStepItem
                          index={stepIndex}
                          navigateTo={`navigate/steps/${stepIndex}?avoidSteps=${elevatorPreferredMode}`}
                          primaryLabel={
                            <Trans
                              components={{
                                category: highlightedStepText,
                                destination: highlightedStepText,
                                landmark: highlightedStepText,
                                intermediary: highlightedStepText,
                                toward: highlightedStepText,
                                turn: highlightedStepText,
                              }}
                            >
                              {t(description.text, {
                                category: t(description.options?.category),
                                destination: t(
                                  description.options?.destination
                                ),
                                landmark: t(description.options?.landmark),
                                intermediary: t(
                                  description.options?.intermediary
                                ),
                                toward: t(description.options?.toward),
                                turn: t(description.options?.turn),
                              })}
                            </Trans>
                          }
                          stepLabelProps={{
                            icon: stepIndex,
                          }}
                          key={`step-description-${index}`}
                        />
                      )
                    })}
                    {destinationData && (
                      <DirectionStepItem
                        primaryLabel={
                          <Trans
                            components={{
                              location: highlightedStepText,
                            }}
                          >
                            {isMultiOrdinalVenue
                              ? t("Location Floor", {
                                  location: t(destinationName),
                                  level: destinationData.level,
                                })
                              : t(destinationName)}
                          </Trans>
                        }
                        secondaryLabel={t("Your Destination")}
                        stepLabelProps={{
                          icon: destinationIconComp,
                        }}
                      />
                    )}
                  </Stepper>
                </Box>
              </Paper>
            </Box>
          </Slide>
        </>
      )}
    </>
  )
}
